import { useState } from 'react';

const own = entry => `custom:${entry}`;

// Where a question opens: on its options (a choice, or a suggestion to confirm),
// or on the input (free text, or a correction the creator already wrote).
export function replyView(question, answers = []) {
  if (!question) return 'input';
  if (question.kind === 'choice') return 'options';
  const saved = answers.find(answer => answer.question_id === question.id);
  const confirmable = question.allowed_responses.includes('confirm') && !!question.suggested_text;
  return confirmable && saved?.kind !== 'answer' ? 'options' : 'input';
}

// Draft answer for the current onboarding question. Its step is keyed by the
// question id, so the draft starts from the saved answer when editing and from
// the analysis' suggestion otherwise. Own entries of a choice are rows like the
// options (`custom:<entry>` in `selected`).
export default function useOnboardingReply(o) {
  const q = o.question;
  const saved = q && o.record.answers.find(answer => answer.question_id === q.id);
  const allows = kind => !!q?.allowed_responses.includes(kind);
  const choice = q?.kind === 'choice';
  const [text, setText] = useState(() => saved?.kind === 'answer' ? saved.text : '');
  const [custom, setCustom] = useState(() => saved?.kind === 'choose' ? saved.custom : []);
  const [selected, setSelected] = useState(() => saved?.kind === 'choose' ? [...saved.options, ...saved.custom.map(own)] : q?.suggested_options || []);
  const [picked, setPicked] = useState(null);
  const typed = text.trim();
  const canSend = !o.busy && (choice ? selected.length > 0 : !!typed && allows('answer'));

  const toggle = key => setSelected(list => list.includes(key) ? list.filter(item => item !== key) : q.multiple ? [...list, key] : [key]);
  // An own entry joins the list chosen (in place of the choice when only one is allowed).
  const addCustom = () => {
    if (!typed || !q.allows_custom) return false;
    setCustom(list => list.includes(typed) ? list : [...list, typed]);
    setSelected(list => q.multiple ? [...list.filter(key => key !== own(typed)), own(typed)] : [own(typed)]);
    setText('');
    return true;
  };
  const send = () => {
    if (!canSend) return false;
    if (!choice) return o.answer(q, { kind: 'answer', text: typed });
    const options = selected.filter(key => q.options.some(option => option.value === key));
    return o.answer(q, { kind: 'choose', options, custom: q.allows_custom ? custom.filter(entry => selected.includes(own(entry))) : [] });
  };
  const confirm = () => { setPicked('confirm'); return o.answer(q, { kind: 'confirm' }); };

  const rows = choice ? [
    ...q.options.map(option => ({ value: option.value, label: option.label, selected: selected.includes(option.value) })),
    ...custom.map(entry => ({ value: own(entry), label: entry, selected: selected.includes(own(entry)) })),
  ] : [];

  return { q, choice, rows, text, setText, toggle, addCustom, canSend, send, picked: o.busy ? picked : null,
    suggestion: q?.kind !== 'choice' && allows('confirm') ? q.suggested_text : null,
    canConfirm: allows('confirm') && !!q.suggested_text, canType: choice ? !!q.allows_custom : allows('answer'), confirm,
    // A correction starts from the suggestion, so a small fix is a small edit.
    correct: () => { if (!text) setText(q.suggested_text || ''); } };
}
